<template>
  <div>
    <b-card title="Ajouter une voiture">
      <validation-observer ref="simpleRules">
        <b-form @submit.prevent="submitForm">
          <!-- Tabs for organizing form fields -->
          <b-tabs content-class="mt-3">
            <!-- Basic Information Tab -->
            <b-tab
              title="Informations de base"
              active
            >
              <b-row>
                <!-- Basic information -->
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label-for="serial_number"
                  >
                    <template #label>
                      Matricule <span class="text-danger">*</span>
                    </template>
                    <validation-provider
                      #default="{ errors: serialNumberErrors }"
                      name="Matricule"
                      rules="required"
                    >
                      <b-form-input
                        id="serial_number"
                        v-model="form.serial_number"
                        :state="serialNumberErrors.length > 0 ? false : null"
                        placeholder="Matricule"
                        required
                      />
                      <small class="text-danger">{{ serialNumberErrors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label-for="brand"
                  >
                    <template #label>
                      Marque <span class="text-danger">*</span>
                    </template>
                    <validation-provider
                      #default="{ errors: brandErrors }"
                      name="Marque"
                      rules="required"
                    >
                      <b-form-input
                        id="brand"
                        v-model="form.brand"
                        :state="brandErrors.length > 0 ? false : null"
                        placeholder="Marque"
                        required
                      />
                      <small class="text-danger">{{ brandErrors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Modèle"
                    label-for="model"
                  >
                    <b-form-input
                      id="model"
                      v-model="form.model"
                      placeholder="Modèle"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Année"
                    label-for="year"
                  >
                    <b-form-input
                      id="year"
                      v-model="form.year"
                      type="number"
                      min="1900"
                      max="2100"
                      placeholder="Année"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label-for="color"
                  >
                    <template #label>
                      Couleur <span class="text-danger">*</span>
                    </template>
                    <validation-provider
                      #default="{ errors: colorErrors }"
                      name="Couleur"
                      rules="required"
                    >
                      <b-form-input
                        id="color"
                        v-model="form.color"
                        :state="colorErrors.length > 0 ? false : null"
                        placeholder="Couleur"
                        required
                      />
                      <small class="text-danger">{{ colorErrors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Numéro de plaque"
                    label-for="plate_number"
                  >
                    <b-form-input
                      id="plate_number"
                      v-model="form.plate_number"
                      placeholder="Numéro de plaque"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Numéro d'identification (VIN)"
                    label-for="vin"
                  >
                    <b-form-input
                      id="vin"
                      v-model="form.vin"
                      placeholder="VIN"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Propriétaire"
                    label-for="owner"
                  >
                    <b-form-input
                      id="owner"
                      v-model="form.owner"
                      placeholder="Propriétaire"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Technical Specifications Tab -->
            <b-tab title="Spécifications techniques">
              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Capacité (m³)"
                    label-for="capacity"
                  >
                    <b-form-input
                      id="capacity"
                      v-model="form.capacity"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Capacité en mètres cubes"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Poids maximal (kg)"
                    label-for="max_weight"
                  >
                    <b-form-input
                      id="max_weight"
                      v-model="form.max_weight"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Poids maximal en kg"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Cylindrée moteur (L)"
                    label-for="engine_size"
                  >
                    <b-form-input
                      id="engine_size"
                      v-model="form.engine_size"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Taille du moteur en litres"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Transmission"
                    label-for="transmission"
                  >
                    <v-select
                      id="transmission"
                      v-model="form.transmission"
                      :options="transmissionOptions"
                      placeholder="Sélectionner type de transmission"
                      :clearable="true"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Type de carburant"
                    label-for="fuel_type"
                  >
                    <v-select
                      id="fuel_type"
                      v-model="form.fuel_type"
                      :options="fuelTypeOptions"
                      placeholder="Sélectionner type de carburant"
                      :clearable="true"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Nombre de portes"
                    label-for="doors"
                  >
                    <b-form-input
                      id="doors"
                      v-model="form.doors"
                      type="number"
                      min="0"
                      placeholder="Nombre de portes"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Nombre de sièges"
                    label-for="seats"
                  >
                    <b-form-input
                      id="seats"
                      v-model="form.seats"
                      type="number"
                      min="0"
                      placeholder="Nombre de sièges"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Maintenance Info Tab -->
            <b-tab title="Informations d'entretien">
              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Kilométrage"
                    label-for="mileage"
                  >
                    <b-form-input
                      id="mileage"
                      v-model="form.mileage"
                      type="number"
                      min="0"
                      placeholder="Kilométrage actuel"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Date d'immatriculation"
                    label-for="registration_date"
                  >
                    <b-form-datepicker
                      id="registration_date"
                      v-model="form.registration_date"
                      placeholder="Date d'immatriculation"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Dernière date d'entretien"
                    label-for="last_maintenance_date"
                  >
                    <b-form-datepicker
                      id="last_maintenance_date"
                      v-model="form.last_maintenance_date"
                      placeholder="Dernière date d'entretien"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Prochaine date d'entretien"
                    label-for="next_maintenance_date"
                  >
                    <b-form-datepicker
                      id="next_maintenance_date"
                      v-model="form.next_maintenance_date"
                      placeholder="Prochaine date d'entretien"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Date d'expiration de l'assurance"
                    label-for="insurance_expiry"
                  >
                    <b-form-datepicker
                      id="insurance_expiry"
                      v-model="form.insurance_expiry"
                      placeholder="Date d'expiration de l'assurance"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Date d'expiration du contrôle technique"
                    label-for="technical_inspection_expiry"
                  >
                    <b-form-datepicker
                      id="technical_inspection_expiry"
                      v-model="form.technical_inspection_expiry"
                      placeholder="Date d'expiration du contrôle technique"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Assignment Tab -->
            <b-tab title="Affectation">
              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Conducteur"
                    label-for="default_driver"
                  >
                    <v-select
                      id="default_driver"
                      v-model="form.default_driver"
                      :options="drivers"
                      label="name"
                      :reduce="driver => driver.id"
                      placeholder="Sélectionner un conducteur"
                      :clearable="true"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Statut"
                    label-for="status"
                  >
                    <v-select
                      id="status"
                      v-model="form.status"
                      :options="statusOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Statut de la voiture"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Rental/Location Information -->
              <b-row class="mt-3">
                <b-col md="12">
                  <div class="rental-section-header">
                    <feather-icon
                      icon="KeyIcon"
                      size="20"
                      class="text-primary mr-2"
                    />
                    <h5 class="mb-0">
                      Informations de location
                    </h5>
                  </div>
                  <hr class="mt-2 mb-3">
                </b-col>
              </b-row>

              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group>
                    <b-form-checkbox
                      id="is_rented"
                      v-model="form.is_rented"
                      name="is_rented"
                      switch
                    >
                      <span class="font-small-3">
                        {{ form.is_rented ? 'Voiture en location' : 'Voiture non louée' }}
                      </span>
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label-for="monthly_rental_amount"
                  >
                    <template #label>
                      <div class="d-flex align-items-center">
                        <feather-icon
                          icon="DollarSignIcon"
                          size="16"
                          class="text-success mr-1"
                        />
                        <span>Montant de location par mois</span>
                      </div>
                    </template>
                    <b-form-input
                      id="monthly_rental_amount"
                      v-model="form.monthly_rental_amount"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Montant mensuel de location"
                      :disabled="!form.is_rented"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col
                  md="12"
                  sm="12"
                >
                  <b-form-group
                    label="Description"
                    label-for="description"
                  >
                    <b-form-textarea
                      id="description"
                      v-model="form.description"
                      rows="3"
                      placeholder="Description de la voiture"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col
                  md="12"
                  sm="12"
                >
                  <b-form-group>
                    <b-form-checkbox
                      id="is_active"
                      v-model="form.is_active"
                      name="is_active"
                      switch
                      size="lg"
                    >
                      {{ form.is_active ? 'Actif' : 'Inactif' }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>

          <b-row class="mt-2">
            <b-col>
              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !isFormValid"
                class="mr-2"
                aria-label="Enregistrer le véhicule"
              >
                <span v-if="isSubmitting">
                  <b-spinner small />
                  Sauvegarde...
                </span>
                <span v-else>Enregistrer</span>
              </b-button>
              <b-button
                variant="outline-secondary"
                :to="{ name: 'car-list' }"
                aria-label="Annuler et retourner à la liste des véhicules"
              >
                Annuler
              </b-button>
            </b-col>
          </b-row>
          <div
            id="car-create-help"
            class="sr-only"
          >
            Remplissez ce formulaire pour ajouter un nouveau véhicule au parc logistique. Les champs obligatoires sont signalés.
          </div>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BFormCheckbox,
  BRow,
  BCol,
  BSpinner,
  BTabs,
  BTab,
  BFormDatepicker,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BFormCheckbox,
    BRow,
    BCol,
    BSpinner,
    BTabs,
    BTab,
    BFormDatepicker,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  data() {
    return {
      form: {
        serial_number: '',
        brand: '',
        model: '',
        year: null,
        color: '',
        plate_number: '',
        vin: '',
        engine_size: null,
        transmission: null,
        fuel_type: null,
        doors: null,
        seats: null,
        capacity: null,
        max_weight: null,
        mileage: null,
        registration_date: null,
        last_maintenance_date: null,
        next_maintenance_date: null,
        insurance_expiry: null,
        technical_inspection_expiry: null,
        owner: '',
        default_driver: null,
        description: '',
        is_rented: false,
        monthly_rental_amount: null,
        status: 'available',
        is_active: true,
      },
      drivers: [],
      isSubmitting: false,
      errors: {},
      transmissionOptions: [
        'Automatic',
        'Manual',
        'Semi-automatic',
        'CVT',
      ],
      fuelTypeOptions: [
        'Gasoline',
        'Diesel',
        'Electric',
        'Hybrid',
        'CNG',
        'LPG',
      ],
      statusOptions: [
        { value: 'available', label: 'Disponible' },
        { value: 'in_use', label: 'En utilisation' },
        { value: 'maintenance', label: 'En maintenance' },
        { value: 'out_of_service', label: 'Hors service' },
      ],
    }
  },
  computed: {
    isFormValid() {
      return (
        this.form.serial_number.trim() !== ''
        && this.form.brand.trim() !== ''
        && this.form.color.trim() !== ''
      )
    },
  },
  watch: {
    'form.is_rented': function handleIsRentedChange(newValue) {
      if (!newValue) {
        this.form.monthly_rental_amount = null
      }
    },
  },
  created() {
    this.loadDrivers()
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async loadDrivers() {
      try {
        const { data } = await axios.get('/api/auth/drivers/', {
          params: { is_active: true },
        })

        this.drivers = data.map(driver => ({
          id: driver.id,
          name: driver.username,
          car: driver.car,
        }))
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des conducteurs')
      }
    },

    async submitForm() {
      const isValid = await this.$refs.simpleRules.validate()
      if (!isValid) return

      try {
        this.isSubmitting = true

        // Format data with snake_case for backend
        const payload = {
          serial_number: this.form.serial_number,
          brand: this.form.brand,
          model: this.form.model || null,
          year: this.form.year ? parseInt(this.form.year, 10) : null,
          color: this.form.color,
          plate_number: this.form.plate_number || null,
          vin: this.form.vin || null,
          engine_size: this.form.engine_size ? parseFloat(this.form.engine_size) : null,
          transmission: this.form.transmission || null,
          fuel_type: this.form.fuel_type || null,
          doors: this.form.doors ? parseInt(this.form.doors, 10) : null,
          seats: this.form.seats ? parseInt(this.form.seats, 10) : null,
          capacity: this.form.capacity ? parseFloat(this.form.capacity) : null,
          max_weight: this.form.max_weight ? parseFloat(this.form.max_weight) : null,
          mileage: this.form.mileage ? parseInt(this.form.mileage, 10) : null,
          registration_date: this.form.registration_date || null,
          last_maintenance_date: this.form.last_maintenance_date || null,
          next_maintenance_date: this.form.next_maintenance_date || null,
          insurance_expiry: this.form.insurance_expiry || null,
          technical_inspection_expiry: this.form.technical_inspection_expiry || null,
          owner: this.form.owner || null,
          default_driver: this.form.default_driver || null,
          description: this.form.description || null,
          is_rented: this.form.is_rented,
          monthly_rental_amount: this.form.monthly_rental_amount ? parseFloat(this.form.monthly_rental_amount) : null,
          status: this.form.status,
          is_active: this.form.is_active,
        }

        await axios.post('/api/cars/create/', payload)

        this.showToast('success', 'top-center', 'Voiture ajoutée avec succès')
        this.$router.push({ name: 'car-list' })
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors = error.response.data
          // eslint-disable-next-line no-console
          console.error('API Error:', error.response.data)
        }
        this.showToast('danger', 'top-center', 'Erreur lors de l\'ajout de la voiture')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Required field asterisk styling
.text-danger {
  font-weight: bold;
}

// Consistent form field sizing
.form-control, .vs__dropdown-toggle {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #FF0D02;
    box-shadow: none;
  }
}

// Textarea exception for height
textarea.form-control {
  height: auto;
}

// Improve v-select styling to match regular inputs
.select-search-option {
  .vs__dropdown-toggle {
    padding: 5px 0;
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  .vs__selected-options {
    padding: 0 6px;
    align-items: center;
  }

  .vs__search {
    padding: 5px 8px;
    margin: 0;
  }

  .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }

  .vs__clear {
    height: 22px;
  }

  .vs__open-indicator {
    height: 17px;
  }
}

// Add styles for the tabs
.nav-tabs {
  border-bottom: 1px solid #ddd;
  margin-bottom: 1rem;

  .nav-link {
    color: #6e6b7b;
    font-weight: 500;

    &.active {
      color: #FF0D02;
      border-bottom: 2px solid #FF0D02;
      border-color: transparent transparent #FF0D02;
    }

    &:hover:not(.active) {
      border-color: transparent;
    }
  }
}

// Modern Rental Information Card Styles
.rental-info-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(102, 126, 234, 0.3);
    transform: translateY(-2px);
  }
}

.rental-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;

  .rental-header-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    transition: all 0.3s ease;

    .rental-icon {
      color: #fff;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }
  }

  .rental-title {
    color: #fff;
    font-weight: 600;
    font-size: 1.25rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.rental-toggle-wrapper {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.rental-status-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  flex-shrink: 0;

  svg {
    color: #9e9e9e;
    transition: all 0.3s ease;
  }

  &.active {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    transform: scale(1.05);

    svg {
      color: #fff;
    }
  }
}

.rental-switch {
  .custom-control-label {
    font-weight: 500;
    color: #374151;
    padding-left: 8px;
  }

  .rental-switch-label {
    font-size: 0.95rem;
    font-weight: 500;
    color: #1f2937;
  }

  .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
  }
}

.rental-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 12px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  animation: slideInUp 0.4s ease;

  svg {
    color: #fff;
  }

  span {
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.rental-amount-wrapper {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.rental-amount-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
  font-size: 0.9rem;

  svg {
    color: #10b981;
  }
}

.rental-amount-input-group {
  position: relative;
  display: flex;
  align-items: center;

  .rental-amount-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transition: all 0.3s ease;

    svg {
      color: #fff;
    }
  }

  .rental-amount-input {
    padding-left: 56px;
    padding-right: 60px;
    height: 48px;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    transition: all 0.3s ease;
    background: #fff;

    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      outline: none;
    }

    &:disabled {
      background: #f9fafb;
      color: #9ca3af;
      cursor: not-allowed;
      border-color: #e5e7eb;

      & ~ .rental-amount-icon {
        background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
      }

      & ~ .rental-amount-currency {
        color: #9ca3af;
      }
    }

    &::placeholder {
      color: #d1d5db;
      font-weight: 500;
    }
  }

  .rental-amount-currency {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: 700;
    font-size: 0.9rem;
    color: #667eea;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transition: all 0.3s ease;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .rental-info-card {
    padding: 20px;
  }

  .rental-header {
    .rental-header-icon {
      width: 40px;
      height: 40px;
      margin-right: 12px;
    }

    .rental-title {
      font-size: 1.1rem;
    }
  }

  .rental-toggle-wrapper,
  .rental-amount-wrapper {
    margin-bottom: 16px;
  }
}
</style>
